import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  inject,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatPaginator, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { MatTable, MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterLink } from '@angular/router';

import { Store } from '@ngrx/store';

import { ListsAddEditModalComponent } from '@components/orders/saved-lists/lists-add-edit-modal/lists-add-edit-modal.component';

import { ConfirmationDialogComponent } from '../../../@core/dialogs/confirmation-dialog/confirmation-dialog.component';
import { OrdersActions } from '../../../@store/orders/orders.actions';
import { SavedLists } from '../../../@store/orders/orders.model';
import {
  selectDeleteListSuccess,
  selectLists,
  selectListsError,
  selectListsLoading,
  selectListsTotalCount,
} from '../../../@store/orders/orders.selectors';
import { CaNumber } from '../../../@store/products/ca-numbers/ca-numbers.model';
import { CONFIG } from '../../../config';
import { PaginationComponent } from '../../@shared/pagination/pagination.component';

@Component({
  selector: 'ciwc-saved-lists',
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatButtonModule,
    MatPaginatorModule,
    MatSortModule,
    MatIconModule,
    MatCardModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    PaginationComponent,
    RouterLink,
  ],
  templateUrl: './saved-lists.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SavedListsComponent implements OnInit {
  private readonly store = inject(Store);
  private readonly dialog = inject(MatDialog);
  private readonly destroyRef = inject(DestroyRef);

  readonly lists = this.store.selectSignal(selectLists);
  readonly listsLength = this.store.selectSignal(selectListsTotalCount);
  readonly listsLoading = this.store.selectSignal(selectListsLoading);
  readonly listsError = this.store.selectSignal(selectListsError);
  readonly deleteListSuccess = this.store.selectSignal(selectDeleteListSuccess);
  readonly CONFIG = CONFIG;

  @ViewChild(MatPaginator, { static: true }) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatTable) table!: MatTable<SavedLists>;

  dataSource: MatTableDataSource<SavedLists> = new MatTableDataSource<SavedLists>();
  displayedColumns: string[] = ['listName', 'dateCreated', 'actions'];
  pageSizeOptions = CONFIG.PAGE_SIZE_OPTIONS;
  pageSize = CONFIG.PAGE_SIZE;
  currentPage = 0;
  totalLists = 0;
  errorText = CONFIG.ERROR_MESSAGES.DEFAULT;
  currentSort: Sort = { active: 'dateCreated', direction: 'desc' };
  deleteListSuccessSignal = signal(false);

  constructor() {
    effect(() => {
      this.initValues();

      if (this.deleteListSuccess() && this.deleteListSuccessSignal()) {
        this.loadListsWithPagination();
        this.deleteListSuccessSignal.set(false);
      }
    });
  }

  ngOnInit() {
    this.fetchData();
  }

  handlePageEvent(event: PageEvent): void {
    this.pageSize = event.pageSize as 10;
    this.currentPage = event.pageIndex;

    if (this.paginator) {
      this.paginator.pageIndex = event.pageIndex;
      this.paginator.pageSize = event.pageSize;
    }

    this.loadListsWithPagination();
  }

  sortData(sort: Sort): void {
    this.currentSort = sort;
    this.loadListsWithPagination();
  }

  private initValues(): void {
    this.dataSource.data = this.lists() || [];

    if (this.listsLength()) {
      this.totalLists = this.listsLength();
    }
  }

  private fetchData(): void {
    this.store.dispatch(
      OrdersActions.loadLists({
        payload: {
          offset: 1,
          limit: 10,
          sort: '-dateCreated',
        },
      }),
    );
  }

  openEditModal(row: SavedLists): void {
    const dialogRef = this.dialog.open(ListsAddEditModalComponent, {
      data: { listItem: row },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.loadListsWithPagination();
      }
    });
  }

  openDeleteModal(row: CaNumber): void {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        message: 'Are you sure you want to delete this list?',
        title: 'Delete List',
        confirmText: 'Delete',
        cancelText: 'Cancel',
      },
      disableClose: true,
      width: '400px',
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (result) {
          this.deleteListSuccessSignal.set(true);
          this.store.dispatch(OrdersActions.deleteList({ id: row.id }));
        }
      });
  }

  private loadListsWithPagination(): void {
    this.store.dispatch(
      OrdersActions.loadLists({
        payload: {
          offset: this.currentPage + 1,
          limit: this.pageSize,
          sort:
            this.currentSort.direction === 'desc'
              ? `-${this.currentSort.active}`
              : this.currentSort.active,
        },
      }),
    );
  }
}
